<template>
  <CCardWithBorder
    v-if="machineImage"
    class="machine-image-card"
    overflow-hidden
  >
    <div
      ref="containerEl"
      class="machine-view-image-container"
    >
      <img
        :src="machineImage"
        :style="{ height: imageHeight ? `${imageHeight}px` : 'auto' }"
        alt="Machine Image"
        class="machine-view-image"
      />
    </div>
  </CCardWithBorder>
</template>

<script setup lang="ts">
import { computed, defineProps, ref, onMounted } from 'vue';
import { VendingMachine } from '@/api/requests/VendingMachines';
import { getUploadUrl } from '@/helpers/urls';
import CCardWithBorder from '../Cards/CCardWithBorder.vue';

const props = defineProps<{
  machine: VendingMachine;
}>();

const containerEl = ref<HTMLElement | null>(null);
const imageHeight = ref<number | null>(null);

const machineImage = computed(() => {
  const imageUrl = props.machine?.attributes.image?.data;

  return imageUrl ? getUploadUrl(imageUrl.attributes.url) : null;
});

const imageActualSize = computed(() => {
  if (!props.machine?.attributes.image?.data) return null;

  return props.machine?.attributes.image?.data.attributes;
});

onMounted(() => {
  if (containerEl.value && imageActualSize.value) {
    const observer = new ResizeObserver(entries => {
      if (!imageActualSize.value || !imageActualSize.value.width || !imageActualSize.value.height) {
        return;
      }

      const entry = entries[0];
      const { width } = entry.contentRect;

      const imageRatio = imageActualSize.value.width / imageActualSize.value.height;

      const height = width / imageRatio;

      imageHeight.value = height;
    });

    observer.observe(containerEl.value);
  }
});
</script>

<style>
.machine-view-image-container {
  display: flex;
  justify-content: center;
  height: 100%;
  align-items: center;
}

.machine-view-image {
  width: 100%;
  height: auto;
  object-fit: cover;
  object-position: center;
  display: block;
  min-height: 100%;
}
</style>
